/**
 * Cover scatter (03 §5.1 step 5): blue-noise placement of low cover and tall vegetation,
 * generous in the approaches, sparse in the belt (a killing field), moderate in the drop
 * band with enough clutter that pod drops need thought. Also reserves the open clearings
 * every approach band must offer for pods (03 §5.2).
 */

import { TAU } from '../sim/math';
import type { Pcg32 } from '../sim/rng';
import type { ScatterKind } from './biomes';
import { NO_BUILD } from './builder';
import { FLAG } from './grid';
import { placeRun } from './obstacles';
import { LIMITS, SCATTER } from './tuning';
import type { Band, GenCtx } from './types';
import { propLoad } from './propLoad';

const NO_SCATTER = NO_BUILD | FLAG.path | FLAG.cover;

/** Hold back a few squares of open ground in each approach band before anything is scattered. */
export function reserveClearings(ctx: GenCtx): void {
  const rng = ctx.rng.fork('clearings');
  const size = SCATTER.clearing;
  for (const band of ctx.bands) {
    if (band.kind !== 'approach') continue;
    let made = 0;
    for (let tries = 0; tries < 60 && made < SCATTER.clearingsPerBand; tries++) {
      const x0 = Math.round(rng.range(4, ctx.sizeX - 4 - size));
      const z0 = Math.round(rng.range(band.z0 + 3, band.z1 - 3 - size));
      if (ctx.map.mask.hitsRect(x0 - 1, z0 - 1, x0 + size + 1, z0 + size + 1, NO_BUILD | FLAG.path)) continue;
      ctx.map.mask.rect(x0, z0, x0 + size - 0.01, z0 + size - 0.01, FLAG.keep);
      made++;
    }
  }
}

function pick(rng: Pcg32, kinds: readonly ScatterKind[]): ScatterKind | null {
  let total = 0;
  for (const k of kinds) total += k.weight;
  let roll = rng.float() * total;
  for (const k of kinds) {
    roll -= k.weight;
    if (roll < 0) return k;
  }
  return kinds[0] ?? null;
}

function scatterBand(ctx: GenCtx, rng: Pcg32, band: Band, wanted: number): void {
  const spacing = SCATTER.spacing[band.kind];
  const c = ctx.plan.compound;
  const compound = ctx.spec.layout === 'compound';
  const z0 = Math.max(2, band.z0);
  // Tall vegetation grows in stands: a few centres per band, trees thrown around them.
  const stands: Array<[number, number]> = [];
  for (let i = 0; i < 3; i++) stands.push([rng.range(8, ctx.sizeX - 8), rng.range(z0 + 3, band.z1 - 3)]);
  const runs = band.kind === 'approach' ? (ctx.kit.coverRun?.perBand ?? 0) : 0;
  for (let i = 0; i < runs; i++) {
    const run = ctx.kit.coverRun;
    if (!run) break;
    const length = Math.round(rng.range(4, 8));
    const x = Math.round(rng.range(6 + length / 2, ctx.sizeX - 6 - length / 2));
    // ROUND7 (WALLS): laid in pieces, so an arctic cover wall breaches like any other wall.
    const z = Math.round(rng.range(z0 + 4, band.z1 - 4));
    placeRun(ctx.map, run.kind, x, z, { length, margin: 2, avoid: NO_SCATTER });
  }

  let placed = 0;
  for (let tries = 0; tries < wanted * 8 && placed < wanted; tries++) {
    const tall = rng.chance(ctx.kit.tallShare);
    const kind = pick(rng, tall ? ctx.kit.tall : ctx.kit.lowCover);
    if (!kind) break;
    let x = rng.range(2.5, ctx.sizeX - 2.5);
    let z = rng.range(z0, band.z1 - 0.5);
    if (tall) {
      const stand = rng.pick(stands);
      x = stand[0] + (rng.float() + rng.float() - 1) * 12;
      z = stand[1] + (rng.float() + rng.float() - 1) * 10;
      if (z < z0 || z > band.z1 - 0.5) continue;
    }
    // The compound is the archetype's to furnish.
    if (compound && x > c.x0 - 1.5 && x < c.x1 + 1.5 && z > c.z0 - 1.5 && z < c.z1 + 1.5) continue;
    const scale = rng.range(kind.scale[0], kind.scale[1]);
    const prop = ctx.map.place(kind.kind, x, z, { scale, yaw: rng.range(0, TAU), margin: 0.7, avoid: NO_SCATTER });
    if (!prop) continue;
    // Trees in a stand crowd together; everything else keeps its distance.
    ctx.map.mask.circle(prop.x, prop.z, tall ? 1.3 : Math.max(0.5, spacing - 1), FLAG.cover);
    placed++;
  }
}

export function scatterCover(ctx: GenCtx): void {
  const rng = ctx.rng.fork('scatter');
  // Walls count by length (propLoad.ts): short breach-sized pieces must not eat the cover.
  const room = Math.max(0, Math.floor(LIMITS.props - propLoad(ctx.map.props)));
  const wanted = ctx.bands.map((b) => (ctx.sizeX * (b.z1 - b.z0) * ctx.kit.density) / SCATTER.areaPerProp[b.kind]);
  const total = wanted.reduce((a, b) => a + b, 0);
  const share = total > room ? room / total : 1;
  ctx.bands.forEach((band, i) => scatterBand(ctx, rng.fork(`band-${i}`), band, Math.floor((wanted[i] ?? 0) * share)));
}
